import * as React from 'react'

import type { MotionProps } from 'motion/react'

import { useMediaQuery } from '@buffer-mono/popcorn'

type SuggestionChipHoverMotion = Pick<
  MotionProps,
  'initial' | 'whileHover' | 'transition'
>

const GRADIENT_BORDER_ANGLES = [0, 90, 180, 270, 360].map(
  (angle) =>
    `linear-gradient(var(--gradient-bg), var(--gradient-bg)) padding-box, linear-gradient(${angle}deg, #567efa, #fb98d5) border-box`,
)

/**
 * The rotating gradient border shared by the AI suggestion chips and by the
 * paywall trigger that replaces them once an organization hits its plan limit.
 * Both read as the same affordance, so the config lives here instead of in
 * either caller. Spread the result onto a motion-enabled `Button`.
 *
 * Under `prefers-reduced-motion` this returns no props at all, which is what
 * lets each caller's static `:hover` gradient take over. Returning only an
 * empty `whileHover` would not: `initial` is written to the element's inline
 * style, and an inline background outranks the stylesheet, so the button would
 * hover to the transparent border its `:hover` rule sets with no gradient
 * behind it.
 */
export function useSuggestionChipHoverMotion(): SuggestionChipHoverMotion {
  const shouldReduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

  return React.useMemo(
    () =>
      shouldReduceMotion
        ? {}
        : {
            initial: { background: 'transparent' },
            whileHover: { background: GRADIENT_BORDER_ANGLES },
            transition: { duration: 2, repeat: Infinity, ease: 'linear' },
          },
    [shouldReduceMotion],
  )
}
